import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faAngleRight } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { BrandXXS } from '@sorare/core/src/atoms/typography';
import { EdgeRectangleTrapezoid } from '@sorare/core/src/atoms/ui/EdgeRectangleTrapezoid';
import { TrophyIcon } from '@sorare/core/src/components/club/Trophies';
import { getEdgeRectangleShapeRarityGradients } from '@sorare/core/src/lib/colors';

import { LineupRewardsDistances } from 'components/lineup/LineupRewardsDistances';
import { SimpleContenderFooterStatus } from 'layouts/LineupLayout/LiveLineupFooter/LeftLiveLineupFooter/SimpleContenderFooterStatus';

import { ContenderBonus } from '../ContenderBonus';
import { ContenderRanking } from '../ContenderRanking';
import { ContenderScore } from '../ContenderScore';
import type { ContenderLiveFooter_so5LeaderboardContender } from './__generated__/index.graphql';

const Main = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1px 1fr;
  padding: var(--intermediate-unit) 0;
  align-items: center;
`;
const Separator = styled.div`
  height: var(--double-unit);
  background-color: var(--c-nd-150);
`;
const StyledButtonBase = styled(ButtonBase)`
  height: 100%;
`;
const StyledLabelS = styled(BrandXXS).attrs({
  uppercase: true,
  brand: true,
})`
  min-height: var(--quadruple-unit);
  display: inline-flex;
  gap: var(--half-unit);
  align-items: center;
  padding: 0 var(--double-and-a-half-unit);
  white-space: nowrap;
`;

const StyledLinkOther = styled(LinkOther)`
  flex: 1;
`;

type Props = {
  so5LeaderboardContender: ContenderLiveFooter_so5LeaderboardContender;
  showBonus?: boolean;
  onClickPrizePool?: () => void;
  customFooter?: ReactNode;
};
export const ContenderLiveFooter = ({
  so5LeaderboardContender,
  showBonus = true,
  onClickPrizePool,
  customFooter,
}: Props) => {
  const { slug, so5Leaderboard, so5Lineup } = so5LeaderboardContender;
  const { so5Rankings, bonusPoints, bonusRating } = so5Lineup || {};

  const ranking = so5Rankings?.at(0);

  const gradientColors = getEdgeRectangleShapeRarityGradients(
    so5Leaderboard.mainRarityType || 'mix'
  );

  return (
    <>
      <StyledLinkOther
        as={onClickPrizePool ? StyledButtonBase : 'div'}
        onClick={onClickPrizePool}
      >
        <Main>
          <Horizontal gap={1} center>
            {!!ranking?.ranking && <TrophyIcon ranking={ranking?.ranking} />}
            <ContenderRanking ranking={ranking?.ranking || null} />
            <FontAwesomeIcon icon={faAngleRight} color="var(--c-nd-600)" />
          </Horizontal>
          <Separator />
          <Horizontal gap={1} center>
            {!!showBonus && (
              <ContenderBonus
                bonusRating={bonusRating || null}
                bonus={bonusPoints || undefined}
              />
            )}
            <ContenderScore score={ranking?.score || null} />
          </Horizontal>
        </Main>
      </StyledLinkOther>
      <Horizontal center>
        <Dropdown
          align="center"
          triggerOnHover
          label={props => (
            <ButtonBase {...props}>
              <EdgeRectangleTrapezoid
                padding="2px var(--unit)"
                offset="20px"
                background={gradientColors.bg}
                border={gradientColors.border}
                hoverBackground={gradientColors.hoverBg}
              >
                {customFooter || (
                  <StyledLabelS color="var(--c-nd-600)">
                    <SimpleContenderFooterStatus
                      so5LeaderboardContender={so5LeaderboardContender}
                    />
                  </StyledLabelS>
                )}
              </EdgeRectangleTrapezoid>
            </ButtonBase>
          )}
        >
          {({ closeDropdown }) => (
            <LineupRewardsDistances
              so5LeaderboardContenderSlug={slug}
              onClose={closeDropdown}
            />
          )}
        </Dropdown>
      </Horizontal>
    </>
  );
};

ContenderLiveFooter.fragments = {
  so5LeaderboardContender: gql`
    fragment ContenderLiveFooter_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Leaderboard {
        slug
        mainRarityType
      }
      so5Lineup {
        id
        bonusPoints
        bonusRating
        so5Rankings {
          id
          ranking
          score
        }
      }
      ...SimpleContenderFooterStatus_so5LeaderboardContender
    }
    ${SimpleContenderFooterStatus.fragments.so5LeaderboardContender}
  ` as TypedDocumentNode<ContenderLiveFooter_so5LeaderboardContender>,
};
